import { Button, Grid2, SxProps, Theme, TypographyProps } from '@mui/material';

import { BUTTON_LABELS } from './constants';
import { useTransferList } from './hooks/useTransferList';
import { TransferListItem } from './types';

export type TransferListProps = {
  /**
   * Unique identifier for the transfer list component.
   */
  id: string;

  /**
   * Callback function to handle changes in the transfer list.
   */
  onChange: (left: TransferListItem[], right: TransferListItem[]) => void;

  /**
   * Title displayed on the left list.
   */
  leftTitle: string;

  /**
   * Items displayed on the left side of the transfer list.
   */
  leftItems: TransferListItem[];

  /**
   * Title displayed on the right list.
   */
  rightTitle: string;

  /**
   * Items displayed on the right side of the transfer list (optional).
   */
  rightItems?: TransferListItem[];

  /**
   * Custom styles for the list components (optional).
   */
  listSx?: SxProps<Theme>;

  /**
   * Custom styles for the list title typography (optional).
   */
  titleTypographyProps?: TypographyProps;

  /**
   * Custom styles for the list subheader typography (optional).
   */
  subheaderTypographyProps?: TypographyProps;

  /**
   * Custom styles for the list item typography (optional).
   */
  listItemTypographyProps?: TypographyProps;
};

export const TransferList = (props: TransferListProps) => {
  const { leftTitle, rightTitle } = props;

  const {
    left,
    leftChecked,
    moveAllToLeft: handleAllLeft,
    moveSelectedToLeft: handleCheckedLeft,

    right,
    rightChecked,
    moveAllToRight: handleAllRight,
    moveSelectedToRight: handleCheckedRight,

    baseButtonProps,
    renderList,
  } = useTransferList(props);

  return (
    <Grid2 container justifyContent="center" alignItems="center" spacing={4}>
      {/* Left list */}
      <Grid2 size={5} sx={{ flexGrow: 1 }}>
        {renderList(leftTitle, left)}
      </Grid2>

      {/* Transfer buttons */}
      <Grid2 container size={1}>
        <Grid2>
          <Button
            {...baseButtonProps}
            onClick={handleAllRight}
            disabled={left.length === 0}
            aria-label="Move all to right"
          >
            {BUTTON_LABELS.MOVE_ALL_RIGHT}
          </Button>
        </Grid2>

        <Button
          {...baseButtonProps}
          onClick={handleCheckedRight}
          disabled={leftChecked.length === 0}
          aria-label="Move selected to right"
        >
          {BUTTON_LABELS.MOVE_SELECTED_RIGHT}
        </Button>

        <Button
          {...baseButtonProps}
          onClick={handleCheckedLeft}
          disabled={rightChecked.length === 0}
          aria-label="Move selected to left"
        >
          {BUTTON_LABELS.MOVE_SELECTED_LEFT}
        </Button>

        <Button
          {...baseButtonProps}
          onClick={handleAllLeft}
          disabled={right.length === 0}
          aria-label="Move all to left"
        >
          {BUTTON_LABELS.MOVE_ALL_LEFT}
        </Button>
      </Grid2>

      {/* Right list */}
      <Grid2 size={5} sx={{ flexGrow: 1 }}>
        {renderList(rightTitle, right)}
      </Grid2>
    </Grid2>
  );
};
